import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loader } from "../components/Loader";
import { useAuth } from "../lib/auth-context";
import { api, setSession } from "../api/client";
import { setSessionUser, type UserRow } from "../api/session";

/**
 * /auth/callback — landing target for redirect-mode OAuth flows.
 *
 * The bot's /api/auth/google-redirect endpoint sends the user here after a
 * successful Google sign-in, with the JWT in the URL fragment (fragments
 * never leave the browser, so they're not logged anywhere). We parse it,
 * persist the session, fetch the user via /api/me, lift them into the
 * shared AuthContext, then navigate to the destination the backend
 * suggested (/register for fresh accounts, /events for returning ones).
 *
 * Error paths (#error=...) render a small message with a "back to login"
 * link instead of getting stuck on a blank screen.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const errorParam = params.get("error");
    if (errorParam) {
      setError(errorParam);
      return;
    }

    const token = params.get("token");
    const next = params.get("next") ?? "/events";
    if (!token) {
      setError("missing_token");
      return;
    }

    setSession(token);
    // Clear the fragment so a refresh of this page doesn't try to reuse the
    // (possibly already-applied) token. replaceState avoids adding a history entry.
    window.history.replaceState(null, "", "/auth/callback");

    (async () => {
      try {
        const me = await api<{ user: UserRow }>("/api/me");
        setSessionUser(me.user);
        setUser(me.user);
        navigate(next, { replace: true });
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-[22px] font-extrabold text-ink-900">{t("authCallback.failedTitle")}</h1>
        <p className="text-[14px] text-ink-500 mt-2 max-w-[340px]">
          {humanizeError(error, t)}
        </p>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="mt-6 px-5 py-2.5 rounded-full bg-brand-purple text-white font-semibold text-[14px]"
        >
          {t("common.tryAgain")}
        </button>
      </div>
    );
  }

  return <Loader text={t("authCallback.loading")} full />;
}

function humanizeError(code: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  switch (code) {
    case "missing_token":
    case "missing_credential":
      return t("authCallback.errors.missing_credential");
    case "invalid_credential":
      return t("authCallback.errors.invalid_credential");
    default:
      return t("authCallback.errors.default", { code });
  }
}
